import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { useLocation, useNavigate } from 'react-router-dom';

import DemoRequestModal from './DemoRequestModal';
import HeroMap from './HeroMap';
import LandingSplash from './LandingSplash';
import SolutionSection from './SolutionSection';
import GapSection from './GapSection';
import CapabilitySection from './CapabilitySection';
import {
  benefits,
  operations,
  closing,
  hero,
} from './landingContent';
import {
  BRAND_FONT,
  C,
  CONTACT_EMAIL,
  LINKEDIN_URL,
  NAVY,
  DECISIONOPT_URL,
  FONT,
  MONO,
  btnPrimarySx,
  btnSmallSx,
  chipSx,
  eyebrowSx,
  h2Sx,
  ledeSx,
  pageZoomVarsSx,
  wrapSx,
  zoomVh,
} from './landingTokens';
import { HEADER_HEIGHT } from './scrollStory';

// MUI pieces inside the page (dialog, inputs) pick up the family font and blue.
const landingTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: C.blue },
    text: { primary: C.ink, secondary: C.body },
    background: { default: C.bg, paper: C.bg },
    divider: C.line,
  },
  typography: { fontFamily: FONT },
});

const publicUrl = process.env.PUBLIC_URL || '';
const LOGO_SRC = `${publicUrl}/transparent_logo.png`;

const pageConfig: Record<string, { title: string; description: string; sectionId?: string }> = {
  '/': {
    title: 'ExPlan | Last-Mile Route Planning and Live Delivery Tracking',
    description:
      'Plan optimised delivery routes around your delivery windows, fleet and depots, review every change, and track each vehicle against the plan. By DecisionOpt.',
  },
  '/platform': {
    title: 'ExPlan Platform | Flexible Inputs, Custom Routing Models, Review Tools',
    description:
      'Explore the ExPlan platform for flexible data inputs, custom routing logic, and planner review workflows.',
    sectionId: 'platform',
  },
};

// The mark is DecisionOpt's, so wherever it appears it links to DecisionOpt.
// Opens in a new tab: it leaves ExPlan, and losing the page mid-visit is worse
// than an extra tab.
const DevelopedBy: React.FC = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9, fontFamily: FONT, whiteSpace: 'nowrap' }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 12, color: C.muted }}>Developed by</Typography>
    <Box
      component="a"
      href={DECISIONOPT_URL}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        textDecoration: 'none',
        color: C.ink,
        borderRadius: 1,
        '&:hover .do-word': { color: C.blue },
        '&:focus-visible': { outline: `2px solid ${C.blue}`, outlineOffset: 2 },
      }}
    >
      <Box component="img" src={LOGO_SRC} alt="" aria-hidden="true" sx={{ height: 34, width: 'auto', mx: -0.75 }} />
      <Typography className="do-word" sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, transition: 'color 150ms ease' }}>
        DecisionOpt
      </Typography>
    </Box>
  </Box>
);

// Product lockup, set the way ScheduleOpt sets its own: Geist, a two-tone
// wordmark ("Ex" + accent "Plan") and a title-case, regular-weight subtitle.
// Plain spans, not Typography: the page root forces its own font on Typography.
const Brand: React.FC<{ tone?: 'light' | 'dark' }> = ({ tone = 'light' }) => {
  const dark = tone === 'dark';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
      <Box
        component="a"
        href={DECISIONOPT_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="DecisionOpt home"
        sx={{
          display: 'inline-flex',
          lineHeight: 0,
          borderRadius: 1,
          flexShrink: 0,
          '&:focus-visible': { outline: `2px solid ${dark ? NAVY.accent : C.blue}`, outlineOffset: 2 },
        }}
      >
        <Box component="img" src={LOGO_SRC} alt="DecisionOpt" sx={{ height: dark ? 66 : 60, width: 'auto', mx: -1.25, my: -1 }} />
      </Box>
      <Box sx={{ ml: 1.25, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.15, fontFamily: BRAND_FONT }}>
        <Box
          component="span"
          sx={{
            fontFamily: BRAND_FONT,
            fontWeight: 700,
            fontSize: dark ? { xs: 22, md: 25 } : 19,
            letterSpacing: '-0.01em',
            color: dark ? '#ffffff' : C.ink,
          }}
        >
          Ex
          <Box component="span" sx={{ color: dark ? NAVY.accent : C.blue }}>
            Plan
          </Box>
        </Box>
        <Box
          component="span"
          sx={{
            fontFamily: BRAND_FONT,
            fontWeight: 400,
            fontSize: dark ? { xs: 13, md: 14.5 } : 12.5,
            color: dark ? NAVY.muted : C.muted,
            whiteSpace: 'nowrap',
            display: dark ? 'block' : { xs: 'none', sm: 'block' },
            mt: 0.25,
          }}
        >
          Last-Mile Route Planning &amp; Tracking
        </Box>
      </Box>
    </Box>
  );
};

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [demoOpen, setDemoOpen] = useState(false);
  const openDemo = () => setDemoOpen(true);

  const goToApp = (event: React.MouseEvent) => {
    event.preventDefault();
    navigate('/login');
  };

  useEffect(() => {
    const config = pageConfig[location.pathname] || pageConfig['/'];
    document.title = config.title;

    const updateMeta = (name: string, content: string) => {
      let tag = document.head.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!tag) {
        tag = document.createElement('meta');
        tag.name = name;
        document.head.appendChild(tag);
      }
      tag.content = content;
    };

    const updatePropertyMeta = (property: string, content: string) => {
      let tag = document.head.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    };

    const canonicalUrl = `https://explan.decisionopt.com${location.pathname === '/' ? '' : location.pathname}`;
    let canonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    updateMeta('description', config.description);
    updatePropertyMeta('og:title', config.title);
    updatePropertyMeta('og:description', config.description);
    updatePropertyMeta('og:url', canonicalUrl);
    updateMeta('twitter:title', config.title);
    updateMeta('twitter:description', config.description);

    if (config.sectionId) {
      const sectionId = config.sectionId;
      requestAnimationFrame(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'auto', block: 'start' });
      });
    } else {
      requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: 'auto' }));
    }
  }, [location.pathname]);

  return (
    <ThemeProvider theme={landingTheme}>
      <LandingSplash />
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: C.bg,
          color: C.ink,
          fontFamily: FONT,
          overflowX: 'clip',
          '& .MuiTypography-root': { fontFamily: FONT },
          // Large screens zoom the page up as a whole (see PAGE_ZOOM_STEPS).
          ...pageZoomVarsSx,
          '& > header, & > main, & > footer': { zoom: 'var(--lp-zoom, 1)' },
          '@keyframes opsMarquee': { to: { transform: 'translateX(-50%)' } },
          '@keyframes rise': {
            from: { opacity: 0, transform: 'translateY(14px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
          '@keyframes routeFlow': {
            from: { strokeDashoffset: 0 },
            to: { strokeDashoffset: -140 },
          },
          '@keyframes livePulse': {
            '0%': { boxShadow: `0 0 0 0 rgba(14,124,116,0.45)` },
            '100%': { boxShadow: `0 0 0 9px rgba(14,124,116,0)` },
          },
          '@media (prefers-reduced-motion: reduce)': {
            '& *, & *::before, & *::after': {
              animation: 'none !important',
              transition: 'none !important',
            },
          },
        }}
      >
        {/* Header */}
        <Box
          component="header"
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 20,
            bgcolor: 'rgba(255,255,255,0.92)',
            backdropFilter: 'saturate(180%) blur(12px)',
            borderBottom: `1px solid ${C.lineSoft}`,
          }}
        >
          {/* Full width like ScheduleOpt's header: only the side gutter, no max width. */}
          <Box sx={{ ...wrapSx, maxWidth: 'none', height: 72, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Brand />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2.5 } }}>
              <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 2.5 }}>
                <DevelopedBy />
                <Box sx={{ width: '1px', height: 30, bgcolor: C.line }} />
              </Box>
              <Box
                component="a"
                href="/login"
                onClick={goToApp}
                sx={{
                  display: { xs: 'none', sm: 'inline' },
                  fontFamily: FONT,
                  fontSize: 14.5,
                  fontWeight: 600,
                  color: C.body,
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                  transition: 'color 150ms ease',
                  '&:hover': { color: C.blue },
                }}
              >
                Sign in
              </Box>
              <Button variant="contained" disableElevation onClick={openDemo} sx={{ ...btnPrimarySx, ...btnSmallSx }}>
                Schedule a demo
              </Button>
            </Box>
          </Box>
        </Box>

        <main>
          {/* Hero, laid out like ScheduleOpt's: copy on white on the left, one
              framed panel (the map card) on the right. Kept deliberately sparse. */}
          {/* On desktop the hero is exactly one screen tall (below the header, capped
              for very tall windows), like ScheduleOpt's; the map card sizes its map
              from the same space, see MAP_HEIGHT in HeroMap.tsx. */}
          <Box component="section" sx={{ borderBottom: `1px solid ${C.lineSoft}` }}>
            <Box
              sx={{
                ...wrapSx,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1.05fr)' },
                gap: { xs: 5, md: 7 },
                alignItems: 'center',
                minHeight: { md: `min(${zoomVh(100, HEADER_HEIGHT)}, 900px)` },
                pt: { xs: 5, md: 5 },
                pb: { xs: 6, md: 5 },
              }}
            >
              <Box sx={{ animation: 'rise .7s cubic-bezier(.16,1,.3,1) both' }}>
                <Box
                  component="ol"
                  aria-label="How ExPlan runs a day"
                  sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'center', rowGap: 0.5, columnGap: 1.5, fontSize: 14 }}
                >
                  {hero.steps.map((step, index) => (
                    <Box component="li" key={step.verb} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, whiteSpace: 'nowrap' }}>
                      {index > 0 && (
                        <Box component="span" aria-hidden="true" sx={{ color: C.subtle }}>
                          →
                        </Box>
                      )}
                      <span>
                        <Box component="span" sx={{ fontWeight: 700, color: C.ink }}>
                          {step.verb}
                        </Box>{' '}
                        <Box component="span" sx={{ color: C.muted }}>
                          {step.note}
                        </Box>
                      </span>
                    </Box>
                  ))}
                </Box>

                <Typography
                  component="h1"
                  sx={{
                    fontFamily: FONT,
                    fontWeight: 800,
                    fontSize: 'clamp(36px, 3.9vw, 56px)',
                    lineHeight: 1.06,
                    letterSpacing: '-0.035em',
                    color: C.ink,
                    mt: 3,
                    maxWidth: 620,
                  }}
                >
                  {hero.titleLine1}{' '}
                  <Box component="span" sx={{ display: 'block', color: C.blue }}>
                    {hero.titleLine2}
                  </Box>
                </Typography>

                <Typography sx={{ ...ledeSx, fontSize: { xs: 16, md: 18 }, mt: 3, maxWidth: 540, textWrap: 'pretty' }}>{hero.lede}</Typography>

                <Button variant="contained" disableElevation onClick={openDemo} sx={{ ...btnPrimarySx, mt: 4 }}>
                  Schedule a demo
                </Button>
                <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: C.muted, mt: 2 }}>{hero.caption}</Typography>
              </Box>

              <Box sx={{ animation: 'rise .8s cubic-bezier(.16,1,.3,1) .12s both', minWidth: 0 }}>
                <HeroMap reduceMotion={reduceMotion} />
              </Box>
            </Box>
          </Box>

          {/* Benefit strip, laid out like ScheduleOpt's: four columns with a left
              divider, icon beside the text, and a slow ticker of the operations ExPlan
              plans for underneath (pauses on hover; stops with reduced motion). */}
          <Box
            component="section"
            aria-label="Why ExPlan"
            sx={{ bgcolor: C.band, borderBottom: `1px solid ${C.lineSoft}`, pt: { xs: 4, md: '44px' }, pb: { xs: 3.5, md: '34px' } }}
          >
            <Box sx={wrapSx}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
                  rowGap: { xs: '22px', md: '28px' },
                }}
              >
                {benefits.map(item => {
                  const Icon = item.icon;
                  return (
                    <Box
                      component="article"
                      key={item.title}
                      sx={{
                        borderLeft: '1px solid #dde2ea',
                        display: 'grid',
                        gridTemplateColumns: '36px minmax(0, 1fr)',
                        gap: '14px',
                        p: '2px 24px 2px 20px',
                      }}
                    >
                      <Box sx={{ ...chipSx(item.tint, 36), borderRadius: '9px' }}>
                        <Icon sx={{ fontSize: 20 }} />
                      </Box>
                      <Box>
                        <Typography component="h3" sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 17, letterSpacing: '-0.015em', lineHeight: 1.25, color: C.ink }}>
                          {item.title}
                        </Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: 14.5, lineHeight: 1.55, color: C.body, mt: '6px' }}>{item.description}</Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              <Box
                sx={{
                  mt: { xs: 3.5, md: '34px' },
                  overflow: 'hidden',
                  WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 10%, #000 90%, transparent)',
                  maskImage: 'linear-gradient(90deg, transparent, #000 10%, #000 90%, transparent)',
                  '&:hover .ops-track': { animationPlayState: 'paused' },
                }}
              >
                <Box className="ops-track" sx={{ display: 'flex', width: 'max-content', animation: 'opsMarquee 46s linear infinite' }}>
                  {[0, 1].map(copy => (
                    <Box
                      component="ul"
                      key={copy}
                      aria-hidden={copy === 1 ? 'true' : undefined}
                      aria-label={copy === 0 ? 'Operations ExPlan plans for' : undefined}
                      sx={{ display: 'flex', flex: 'none', listStyle: 'none', m: 0, p: 0 }}
                    >
                      {operations.map(op => (
                        <Box
                          component="li"
                          key={op}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '20px',
                            pr: '20px',
                            fontFamily: MONO,
                            fontSize: 13.5,
                            letterSpacing: '0.06em',
                            color: '#6e7686',
                            whiteSpace: 'nowrap',
                            '&::before': { content: '""', width: 5, height: 5, borderRadius: '50%', bgcolor: '#93a8fb' },
                          }}
                        >
                          {op}
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* The gap: scroll-driven route that breaks in four places. */}
          <GapSection />

          {/* The solution: sticky index and five parts (long-form, static). */}
          <SolutionSection />

          {/* Engine capabilities, laid out like ScheduleOpt's Results grid. */}
          <CapabilitySection />

          {/* Get started: three steps on one thin line, with "Schedule a demo" as the
              last stop. Dark band that runs straight into the navy footer. */}
          <Box
            component="section"
            id="cta"
            sx={{ scrollMarginTop: '72px', py: 'clamp(72px, 9vw, 112px)', bgcolor: NAVY.bg }}
          >
            <Box sx={wrapSx}>
              <Typography sx={{ ...eyebrowSx, color: '#8fb2ff' }}>Get started</Typography>
              <Typography component="h2" sx={{ ...h2Sx, color: '#ffffff', fontSize: 'clamp(28px, 3vw, 40px)', mt: 1.5 }}>
                {closing.title}
              </Typography>
              <Typography sx={{ ...ledeSx, color: NAVY.text, mt: 2, maxWidth: 560 }}>{closing.lede}</Typography>

              <Box sx={{ position: 'relative', mt: { xs: 5, md: 7 } }}>
                {/* The route: horizontal on desktop, down the left edge on phones. */}
                <Box
                  aria-hidden="true"
                  sx={{
                    position: 'absolute',
                    bgcolor: 'rgba(148,163,184,0.35)',
                    top: { xs: 6, md: 6 },
                    bottom: { xs: 80, md: 'auto' },
                    left: 6,
                    right: { xs: 'auto', md: 260 },
                    width: { xs: '1px', md: 'auto' },
                    height: { xs: 'auto', md: '1px' },
                  }}
                />
                <Box
                  component="ol"
                  sx={{
                    position: 'relative',
                    listStyle: 'none',
                    m: 0,
                    p: 0,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr)) 240px' },
                    gap: { xs: 4, md: 3 },
                    alignItems: 'start',
                  }}
                >
                  {closing.steps.map((step, index) => (
                    <Box
                      component="li"
                      key={step.title}
                      sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'column' }, gap: { xs: 2.5, md: 0 } }}
                    >
                      <Box
                        aria-hidden="true"
                        sx={{
                          width: 13,
                          height: 13,
                          flexShrink: 0,
                          boxSizing: 'border-box',
                          borderRadius: '50%',
                          bgcolor: NAVY.bg,
                          border: '2px solid #8fb2ff',
                          mt: { xs: '4px', md: 0 },
                        }}
                      />
                      <Box sx={{ mt: { xs: 0, md: 2.75 } }}>
                        <Typography sx={{ fontFamily: MONO, fontSize: 12, letterSpacing: '0.1em', color: NAVY.muted }}>
                          {String(index + 1).padStart(2, '0')}
                        </Typography>
                        <Typography component="h3" sx={{ fontFamily: FONT, fontSize: 17, fontWeight: 700, color: '#ffffff', mt: 1 }}>
                          {step.title}
                        </Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: 14, lineHeight: 1.55, color: NAVY.text, mt: 0.75 }}>
                          {step.detail}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                  <Box component="li" sx={{ mt: { xs: 1, md: '-21px' }, pl: { xs: '33px', md: 0 } }}>
                    <Button
                      variant="contained"
                      disableElevation
                      onClick={openDemo}
                      endIcon={<ArrowForwardRoundedIcon />}
                      sx={{
                        ...btnPrimarySx,
                        width: { xs: 'auto', md: '100%' },
                        bgcolor: '#2f63d6',
                        '&:hover': { bgcolor: '#3b72ea', boxShadow: 'none' },
                      }}
                    >
                      Schedule a demo
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </main>

        {/* Footer: same layout and navy band as the ScheduleOpt footer. */}
        <Box component="footer" sx={{ bgcolor: NAVY.bg, borderTop: `1px solid ${NAVY.line}` }}>
          <Box sx={{ ...wrapSx, pt: '44px', pb: '32px' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 3 }}>
              <Brand tone="dark" />
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                <Box
                  component="a"
                  href={`mailto:${CONTACT_EMAIL}`}
                  sx={{ fontFamily: BRAND_FONT, fontSize: 15, color: NAVY.text, textDecoration: 'none', '&:hover': { color: '#ffffff' } }}
                >
                  {CONTACT_EMAIL}
                </Box>
                <Box
                  component="a"
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="DecisionOpt on LinkedIn"
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    color: '#ffffff',
                    border: '1px solid rgba(195,206,222,0.3)',
                    transition: 'border-color .2s, background-color .2s',
                    '&:hover': { borderColor: NAVY.accent, bgcolor: 'rgba(101,128,242,0.15)' },
                    '&:focus-visible': { outline: `2px solid ${NAVY.accent}`, outlineOffset: 2 },
                  }}
                >
                  <Box component="svg" viewBox="0 0 24 24" aria-hidden="true" sx={{ width: 17, height: 17 }}>
                    <path
                      fill="currentColor"
                      d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9.75h4v11H3v-11Zm7 0h3.8v1.6h.06c.53-.95 1.83-1.95 3.77-1.95 4.03 0 4.77 2.5 4.77 5.75v5.6h-4v-4.97c0-1.19-.02-2.71-1.75-2.71-1.75 0-2.02 1.29-2.02 2.62v5.06H10v-11Z"
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                mt: '30px',
                pt: '22px',
                borderTop: `1px solid ${NAVY.line}`,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                columnGap: '20px',
                rowGap: 1,
                fontFamily: BRAND_FONT,
                fontSize: 14.5,
              }}
            >
              <Box component="span" sx={{ color: NAVY.muted, mr: 'auto' }}>
                © {new Date().getFullYear()} DecisionOpt. All rights reserved.
              </Box>
              <Box sx={{ display: 'inline-flex', gap: '22px' }}>
                {[
                  { label: 'Privacy Policy', href: `${DECISIONOPT_URL}/privacy-policy` },
                  { label: 'Terms & Conditions', href: `${DECISIONOPT_URL}/terms-and-conditions` },
                ].map(link => (
                  <Box
                    key={link.href}
                    component="a"
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: NAVY.text, textDecoration: 'none', '&:hover': { color: '#ffffff' } }}
                  >
                    {link.label}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>

        <DemoRequestModal open={demoOpen} onClose={() => setDemoOpen(false)} />
      </Box>
    </ThemeProvider>
  );
};

export default LandingPage;
